import type { Controller } from "../lifecycle";

interface HtmxErrorEvent extends Event {
  detail?: { elt?: HTMLElement };
}

export class PwaOfflineController implements Controller {
  private indicator?: HTMLElement;
  private status?: HTMLElement;
  private cacheButton?: HTMLButtonElement;

  private readonly update = (): void => {
    const offline = !navigator.onLine;
    if (this.indicator) {
      this.indicator.hidden = !offline;
    }
    if (this.status) {
      this.status.textContent = offline
        ? "You are offline. Cached pages remain available."
        : "Connection restored.";
    }
  };

  private readonly onHtmxError = (event: Event): void => {
    const target = (event as HtmxErrorEvent).detail?.elt;
    const region =
      target?.closest<HTMLElement>("[data-chapter]")?.querySelector<HTMLElement>(
        ".chapter__detail"
      ) ?? document.querySelector<HTMLElement>("#app-status");

    if (region) {
      region.innerHTML =
        '<aside class="inline-offline" role="status"><strong>The network is quiet.</strong><span>This detail is not cached yet. Return online and try once more.</span></aside>';
    }
  };

  private readonly cacheFilm = async (): Promise<void> => {
    if (!this.cacheButton || !("serviceWorker" in navigator)) return;
    this.cacheButton.disabled = true;
    this.cacheButton.textContent = "Saving…";
    if (this.status) {
      this.status.textContent =
        "Saving the web film for offline playback. This download is about 15 megabytes.";
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      registration.active?.postMessage({
        type: "CACHE_MEDIA",
        url: "/wallpaperdemo/media/unquiet-walls-film.mp4"
      });
    } catch {
      this.cacheButton.disabled = false;
      this.cacheButton.textContent = "Save 15 MB";
      if (this.status) {
        this.status.textContent =
          "The film could not be saved. Check the connection and try again.";
      }
    }
  };

  private readonly onWorkerMessage = (event: MessageEvent): void => {
    const data = event.data as { type?: string; ok?: boolean };
    if (data.type !== "MEDIA_CACHED") return;
    if (this.cacheButton) {
      this.cacheButton.disabled = false;
      this.cacheButton.textContent = data.ok ? "Film saved" : "Try again";
      this.cacheButton.dataset.state = data.ok ? "success" : "error";
    }
    if (this.status) {
      this.status.textContent = data.ok
        ? "The film is available for offline playback."
        : "The film could not be saved. Check the connection and try again.";
    }
  };

  mount(root: ParentNode): void {
    this.indicator =
      document.querySelector<HTMLElement>("#network-status") ?? undefined;
    this.status = document.querySelector<HTMLElement>("#app-status") ?? undefined;
    this.cacheButton =
      root.querySelector<HTMLButtonElement>("[data-cache-film]") ?? undefined;
    window.addEventListener("online", this.update);
    window.addEventListener("offline", this.update);
    document.body.addEventListener("htmx:responseError", this.onHtmxError);
    document.body.addEventListener("htmx:sendError", this.onHtmxError);
    navigator.serviceWorker?.addEventListener("message", this.onWorkerMessage);
    this.cacheButton?.addEventListener("click", this.cacheFilm);
    this.update();
  }

  destroy(): void {
    window.removeEventListener("online", this.update);
    window.removeEventListener("offline", this.update);
    document.body.removeEventListener("htmx:responseError", this.onHtmxError);
    document.body.removeEventListener("htmx:sendError", this.onHtmxError);
    navigator.serviceWorker?.removeEventListener("message", this.onWorkerMessage);
    this.cacheButton?.removeEventListener("click", this.cacheFilm);
    this.indicator = undefined;
    this.status = undefined;
    this.cacheButton = undefined;
  }
}
